import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { api, formatApiError } from "../lib/api";
import { ROLE_LABELS, FULL_ACCESS_ROLES, COMMERCIAL_ROLES, CARE_ROLES, PARTNER_ROLES, GROUP_WIDE_ROLES, SOCIAL_LIFE_ROLES, GROUP_TYPES, GROUP_TYPE_STYLE, PROFESSIONAL_ROLES, KITCHEN_ROLES, PROFESSIONAL_FUNCTIONS, EXTRA_GRANTABLE_MODULES } from "../lib/constants";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogFooter, AlertDialogTitle, AlertDialogDescription, AlertDialogAction, AlertDialogCancel } from "../components/ui/alert-dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { Users2, Plus, KeyRound, Power, Trash2, Copy, Clock, ShieldCheck, Building2, Mail, Send, Pencil, Layers, Users, ChevronRight, UserPlus, Search, RotateCcw } from "lucide-react";
import { useNavigate } from "react-router-dom";

function Credentials({ creds, onClose }) {
  const { t, i18n } = useTranslation();
  const locale = (i18n.language || "fr").startsWith("nl") ? "nl-BE" : (i18n.language || "fr").startsWith("en") ? "en-GB" : "fr-BE";
  const copy = () => {
    navigator.clipboard.writeText(`${t("users.username")}: ${creds.username}\n${t("users.password")}: ${creds.temp_password}`);
    toast.success(t("users.copied"));
  };
  return (
    <Dialog open={!!creds} onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader><DialogTitle className="font-display flex items-center gap-2"><ShieldCheck className="h-5 w-5 text-emerald-400" /> {t("users.credentialsCreated")}</DialogTitle></DialogHeader>
        <div className="py-2 space-y-3">
          <p className="text-sm text-muted-foreground">{t("users.credIntro")}</p>
          <div className="glass rounded-xl p-4 space-y-2 font-mono">
            <div className="flex justify-between"><span className="text-muted-foreground text-sm">{t("users.username")}</span><span className="font-bold tracking-widest" data-testid="created-username">{creds.username}</span></div>
            <div className="flex justify-between"><span className="text-muted-foreground text-sm">{t("users.password")}</span><span className="font-bold tracking-widest" data-testid="created-password">{creds.temp_password}</span></div>
          </div>
          <p className="text-xs text-amber-300 flex items-center gap-1.5"><Clock className="h-3.5 w-3.5" /> {t("users.validDays", { date: new Date(creds.temp_expires_at).toLocaleDateString(locale) })}</p>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={copy}><Copy className="h-4 w-4 mr-2" /> {t("users.copy")}</Button>
          <Button onClick={onClose}>{t("users.done")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const REGIONS = [
  ["flandre", "Région flamande"],
  ["wallonie", "Région wallonne"],
  ["bruxelles", "Région de Bruxelles-Capitale"],
];
const regionLabel = (r) => (REGIONS.find(([v]) => v === r) || [null, ""])[1];

export default function Utilisateurs() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const roleLabel = (r) => t(`enums.role.${r}`, { defaultValue: ROLE_LABELS[r] || r });
  const GROUP_ROLES = ["ceo"];
  const MAISON_FULL_ROLES = ["directeur", "directeur_adjoint"];
  const renderRoleOptions = () => (
    <>
      <div className="px-2 py-1 text-[11px] uppercase tracking-wider text-emerald-300/80">{t("users.roleSection.group", { defaultValue: "Accès GROUPE — toutes les maisons du groupe" })}</div>
      {GROUP_ROLES.map((r) => <SelectItem key={r} value={r}>{roleLabel(r)}</SelectItem>)}
      <div className="px-2 py-1 mt-1 text-[11px] uppercase tracking-wider text-cyan-300/80">{t("users.roleSection.commercial", { defaultValue: "Accès GROUPE commercial — TdB, Agenda, Suivi commercial" })}</div>
      {COMMERCIAL_ROLES.map((r) => <SelectItem key={r} value={r}>{roleLabel(r)}</SelectItem>)}
      <div className="px-2 py-1 mt-1 text-[11px] uppercase tracking-wider text-teal-300/80">{t("users.roleSection.socialLife", { defaultValue: "Accès GROUPE vie sociale — Agenda, Planning, Vie sociale, Messagerie" })}</div>
      {SOCIAL_LIFE_ROLES.map((r) => <SelectItem key={r} value={r}>{roleLabel(r)}</SelectItem>)}
      <div className="px-2 py-1 mt-1 text-[11px] uppercase tracking-wider text-muted-foreground">{t("users.roleSection.maison", { defaultValue: "Accès MAISON — leur maison uniquement" })}</div>
      {MAISON_FULL_ROLES.map((r) => <SelectItem key={r} value={r}>{roleLabel(r)}</SelectItem>)}
      <div className="px-2 py-1 mt-1 text-[11px] uppercase tracking-wider text-muted-foreground">{t("users.careGroup")}</div>
      {CARE_ROLES.map((r) => <SelectItem key={r} value={r}>{roleLabel(r)}</SelectItem>)}
      <div className="px-2 py-1 mt-1 text-[11px] uppercase tracking-wider text-violet-300/80">{t("users.roleSection.professional", { defaultValue: "Personnel PARAMÉDICAL / ANIMATION — Planning activité + Agenda uniquement" })}</div>
      {PROFESSIONAL_ROLES.map((r) => <SelectItem key={r} value={r}>{roleLabel(r)}</SelectItem>)}
      <div className="px-2 py-1 mt-1 text-[11px] uppercase tracking-wider text-orange-300/80">{t("users.roleSection.kitchen", { defaultValue: "CUISINE — module Cuisine + Messagerie uniquement" })}</div>
      {KITCHEN_ROLES.map((r) => <SelectItem key={r} value={r}>{roleLabel(r)}</SelectItem>)}
    </>
  );
  const [users, setUsers] = useState([]);
  const [maisons, setMaisons] = useState([]);
  const [groups, setGroups] = useState([]);
  const [newGroup, setNewGroup] = useState({ name: "", type: "prive" });
  const [newMaisonGroup, setNewMaisonGroup] = useState("");
  const [editGroup, setEditGroup] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", role: "directeur", maison: "", group_id: "", email: "", language: "fr", function: "", extra_allowed_paths: [] });
  const [creds, setCreds] = useState(null);
  const [newMaison, setNewMaison] = useState("");
  const [reminderOpen, setReminderOpen] = useState(false);
  const [reminderEmail, setReminderEmail] = useState("");
  const [renameTarget, setRenameTarget] = useState(null);
  const [renameValue, setRenameValue] = useState("");
  const [renameGroup, setRenameGroup] = useState("");
  const [mRegion, setMRegion] = useState("");
  const [mCommune, setMCommune] = useState("");
  const [mWebsite, setMWebsite] = useState("");
  const [mPhone, setMPhone] = useState("");
  const [editUser, setEditUser] = useState(null);
  const [editForm, setEditForm] = useState({ name: "", role: "directeur", maison: "", group_id: "", email: "", language: "fr", function: "", extra_allowed_paths: [] });
  const [selectedGroupId, setSelectedGroupId] = useState(localStorage.getItem("activeGroupId") || "all");
  const [highlightId, setHighlightId] = useState(null);
  const [drillMaison, setDrillMaison] = useState(null);

  const load = async () => {
    const [u, m, g] = await Promise.all([api.get("/users"), api.get("/maisons"), api.get("/groups")]);
    setUsers(u.data); setMaisons(m.data); setGroups(g.data);
  };
  useEffect(() => { load(); }, []);
  useEffect(() => {
    const h = sessionStorage.getItem("highlightUser");
    if (h) { setHighlightId(h); setSelectedGroupId("all"); sessionStorage.removeItem("highlightUser");
      setTimeout(() => setHighlightId(null), 4000);
    }
    const onGroup = () => setSelectedGroupId(localStorage.getItem("activeGroupId") || "all");
    window.addEventListener("carepilot:group", onGroup);
    return () => window.removeEventListener("carepilot:group", onGroup);
  }, []);
  const selectGroup = (gid) => { setSelectedGroupId(gid); localStorage.setItem("activeGroupId", gid); window.dispatchEvent(new Event("carepilot:group")); };

  const typeLabel = (tp) => t(`users.groups.type.${tp}`, { defaultValue: GROUP_TYPES[tp] || tp });

  const addGroup = async () => {
    if (!newGroup.name.trim()) return toast.error(t("users.groups.groupName"));
    try {
      await api.post("/groups", { name: newGroup.name.trim(), type: newGroup.type });
      setNewGroup({ name: "", type: "prive" }); toast.success(t("users.maisonAdded")); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const saveGroup = async () => {
    if (!editGroup?.name.trim()) return toast.error(t("users.groups.groupName"));
    try {
      await api.put(`/groups/${editGroup.id}`, { name: editGroup.name.trim(), type: editGroup.type });
      toast.success(t("users.maisonRenamed")); setEditGroup(null); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const delGroup = async (g) => {
    if (!window.confirm(t("users.groups.delGroupConfirm"))) return;
    try { await api.delete(`/groups/${g.id}`); toast.success(t("users.maisonDeleted")); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const create = async () => {
    const groupWide = GROUP_WIDE_ROLES.includes(form.role);
    if (!form.name.trim()) return toast.error(t("users.nameRequired"));
    if (groupWide ? !form.group_id : !form.maison) return toast.error(t("users.nameMaisonRequired"));
    try {
      const body = { name: form.name, role: form.role, email: form.email, language: form.language };
      if (groupWide) body.group_id = form.group_id; else body.maison = form.maison;
      if (PROFESSIONAL_ROLES.includes(form.role)) { body.function = form.function || ""; body.extra_allowed_paths = form.extra_allowed_paths || []; }
      const { data } = await api.post("/users", body);
      setCreds(data);
      setOpen(false);
      setForm({ name: "", role: "directeur", maison: "", group_id: "", email: "", language: "fr", function: "", extra_allowed_paths: [] });
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const reset = async (u) => { const { data } = await api.post(`/users/${u.id}/reset-password`); setCreds(data); };
  const [resending, setResending] = useState(null);
  const [confirmResend, setConfirmResend] = useState(null);
  const resendCreds = async (u) => {
    if (!u.email) { toast.error(t("users.resendNoEmail", { defaultValue: "Aucune adresse e-mail renseignée pour cet utilisateur." })); return; }
    setResending(u.id);
    try {
      await api.post(`/users/${u.id}/resend-credentials`);
      toast.success(t("users.resendOk", { email: u.email, defaultValue: `Identifiants renvoyés à ${u.email}` }));
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setResending(null); }
  };

  const openEdit = (u) => {
    setEditUser(u);
    setEditForm({ name: u.name || "", role: u.role, maison: u.maison || "", group_id: u.group_id || "", email: u.email || "", language: u.language || "fr", function: u.function || "", extra_allowed_paths: u.extra_allowed_paths || [] });
  };
  const saveEdit = async () => {
    const groupWide = GROUP_WIDE_ROLES.includes(editForm.role);
    try {
      const body = { name: editForm.name, role: editForm.role, email: editForm.email, language: editForm.language };
      if (groupWide) body.group_id = editForm.group_id; else body.maison = editForm.maison;
      if (PROFESSIONAL_ROLES.includes(editForm.role)) { body.function = editForm.function || ""; body.extra_allowed_paths = editForm.extra_allowed_paths || []; }
      else { body.extra_allowed_paths = []; }
      await api.put(`/users/${editUser.id}`, body);
      toast.success(t("users.updated", { defaultValue: "Utilisateur mis à jour" }));
      setEditUser(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };
  const toggle = async (u) => { await api.patch(`/users/${u.id}/toggle`); load(); };
  const remove = async (u) => { await api.delete(`/users/${u.id}`); toast.success(t("users.deleted")); load(); };

  const openCreateMaison = () => {
    const gid = selectedGroupId !== "all" ? selectedGroupId : newMaisonGroup;
    setRenameTarget({ id: null });
    setRenameValue(newMaison.trim());
    setRenameGroup(gid || "");
    setMRegion(""); setMCommune(""); setMWebsite(""); setMPhone("");
  };
  const delMaison = async (m) => {
    try { await api.delete(`/maisons/${m.id}`); toast.success(t("users.maisonDeleted")); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const resetMaison = async (m) => {
    if (!window.confirm(t("users.resetMaisonConfirm", { name: m.name, defaultValue: `Réinitialiser « ${m.name} » ?\n\nCela videra tous les prospects, visites, relances, admissions et demandes partenaires de cette maison. Les chambres seront conservées et remises à « Libre ». Action irréversible.` }))) return;
    try {
      const r = await api.post(`/crm/admin/reset-maison/${m.id}`);
      toast.success(t("users.resetMaisonDone", { defaultValue: "Maison réinitialisée." }) + ` (${r.data.deleted || 0} éléments supprimés)`);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const openRename = (m) => {
    setRenameTarget(m); setRenameValue(m.name); setRenameGroup(m.group_id || "");
    setMRegion(m.region || ""); setMCommune(m.commune || ""); setMWebsite(m.website || ""); setMPhone(m.phone || "");
  };
  const saveMaison = async () => {
    const name = renameValue.trim();
    if (!name) return toast.error(t("users.nameRequired"));
    const gid = renameGroup || renameTarget?.group_id;
    if (!gid) return toast.error(t("users.groups.groupRequired"));
    const body = { name, group_id: gid, region: mRegion, commune: mCommune.trim(), website: mWebsite.trim(), phone: mPhone.trim() };
    try {
      if (renameTarget?.id) await api.put(`/maisons/${renameTarget.id}`, body);
      else { await api.post("/maisons", body); setNewMaison(""); }
      toast.success(renameTarget?.id ? t("users.maisonRenamed") : t("users.maisonAdded"));
      setRenameTarget(null); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const sendReminders = async () => {
    try {
      const body = reminderEmail.trim() ? { recipient_email: reminderEmail.trim() } : {};
      const { data } = await api.post("/reminders/send", body);
      toast.success(t("users.reminderSent", { count: data.count, n: data.sent.length }));
      if (data.errors?.length) toast.error(t("users.reminderErrors", { n: data.errors.length }));
      setReminderOpen(false); setReminderEmail("");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const visibleMaisons = selectedGroupId === "all" ? maisons : maisons.filter((m) => m.group_id === selectedGroupId);
  const visibleUsers = selectedGroupId === "all" ? users : users.filter((u) => u.group_id === selectedGroupId);
  const selGroup = groups.find((g) => g.id === selectedGroupId);
  const userCountOf = (gid) => users.filter((u) => u.group_id === gid).length;
  const maisonCountOf = (gid) => maisons.filter((m) => m.group_id === gid).length;
  const openCreateUser = () => { setForm({ name: "", role: "directeur", maison: "", group_id: selectedGroupId !== "all" ? selectedGroupId : "", email: "", language: "fr", function: "", extra_allowed_paths: [] }); setOpen(true); };
  return (
    <div className="p-6 lg:p-10 max-w-[1800px] mx-auto" data-testid="admin-users-page">
      <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4">
        <div data-tour="users-header">
          <p className="text-xs uppercase tracking-[0.25em] font-semibold text-indigo-300/80">{t("users.tag")}</p>
          <h1 className="font-display text-4xl lg:text-5xl font-black tracking-tight mt-2 flex items-center gap-3">
            <span className="h-12 w-12 rounded-2xl flex items-center justify-center bg-indigo-500/15 text-indigo-300 border border-indigo-400/20"><Users2 className="h-6 w-6" /></span>
            {t("users.title")}
          </h1>
        </div>
        <div className="flex items-center gap-2">
          <Button className="h-11 rounded-full px-5 gap-2" onClick={openCreateUser} data-testid="add-user-btn" data-tour="users-add"><Plus className="h-4 w-4" /> {t("users.createUser")}</Button>
        </div>
      </div>

      {/* Group selector strip */}
      <div className="glass rounded-2xl card-3d p-5 mt-8 relative overflow-hidden" data-tour="users-maisons">
        <span className="dash-glow -right-10 -top-10 h-40 w-40" style={{ background: "radial-gradient(circle, rgba(139,92,246,0.22), transparent 70%)" }} />
        <div className="relative flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <h2 className="font-display font-bold flex items-center gap-2 text-sm uppercase tracking-wider text-violet-300"><Layers className="h-4 w-4" /> {t("users.groups.groups")}</h2>
          <div className="flex flex-col sm:flex-row gap-2">
            <Input value={newGroup.name} onChange={(e) => setNewGroup({ ...newGroup, name: e.target.value })} placeholder={t("users.groups.groupNamePlaceholder")} data-testid="new-group-input" className="h-10 sm:w-52" />
            <Select value={newGroup.type} onValueChange={(v) => setNewGroup({ ...newGroup, type: v })}>
              <SelectTrigger className="h-10 sm:w-40" data-testid="new-group-type"><SelectValue /></SelectTrigger>
              <SelectContent>{Object.keys(GROUP_TYPES).map((tp) => <SelectItem key={tp} value={tp}>{typeLabel(tp)}</SelectItem>)}</SelectContent>
            </Select>
            <Button className="h-10 bg-violet-600 hover:bg-violet-500 shrink-0 gap-1.5" onClick={addGroup} data-testid="add-group-btn"><Plus className="h-4 w-4" /> {t("users.groups.addGroup")}</Button>
          </div>
        </div>
        <div className="relative grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 mt-5">
          <button onClick={() => selectGroup("all")} data-testid="group-select-all"
            className={`text-left rounded-2xl p-4 border transition-colors ${selectedGroupId === "all" ? "border-indigo-400/60 bg-indigo-500/10" : "border-white/10 bg-white/[0.03] hover:border-white/25"}`}>
            <div className="flex items-center justify-between">
              <span className="font-display font-bold flex items-center gap-2"><Users className="h-4 w-4 text-indigo-300" /> {t("users.allGroups", { defaultValue: "Tous les groupes" })}</span>
              <ChevronRight className={`h-4 w-4 ${selectedGroupId === "all" ? "text-indigo-300" : "text-muted-foreground"}`} />
            </div>
            <p className="text-xs text-muted-foreground mt-2">{groups.length} · {maisons.length} maisons · {users.length} utilisateurs</p>
          </button>
          {groups.map((g) => {
            const st = GROUP_TYPE_STYLE[g.type] || GROUP_TYPE_STYLE.autre;
            const active = selectedGroupId === g.id;
            return (
              <div key={g.id} onClick={() => selectGroup(g.id)} data-testid={`group-card-${g.id}`}
                className={`cursor-pointer rounded-2xl p-4 border transition-colors ${active ? "border-indigo-400/60 bg-indigo-500/10" : "border-white/10 bg-white/[0.03] hover:border-white/25"}`}>
                <div className="flex items-start justify-between gap-2">
                  <span className="font-display font-bold truncate">{g.name}</span>
                  <div className="flex items-center gap-1 shrink-0">
                    <button onClick={(e) => { e.stopPropagation(); setEditGroup({ id: g.id, name: g.name, type: g.type }); }} className="h-6 w-6 rounded-full hover:bg-white/10 flex items-center justify-center" data-testid={`edit-group-${g.id}`}><Pencil className="h-3 w-3" /></button>
                    <button onClick={(e) => { e.stopPropagation(); delGroup(g); }} className="h-6 w-6 rounded-full hover:bg-red-500/20 hover:text-red-400 flex items-center justify-center" data-testid={`del-group-${g.id}`}><Trash2 className="h-3 w-3" /></button>
                  </div>
                </div>
                <div className="flex items-center gap-2 mt-2">
                  <span className="text-[10px] font-semibold rounded-full px-2 py-0.5" style={{ background: st.bg, color: st.text }}>{typeLabel(g.type)}</span>
                </div>
                <p className="text-xs text-muted-foreground mt-2 flex items-center gap-3">
                  <span className="inline-flex items-center gap-1"><Building2 className="h-3 w-3" /> {maisonCountOf(g.id)}</span>
                  <span className="inline-flex items-center gap-1"><Users className="h-3 w-3" /> {userCountOf(g.id)}</span>
                </p>
              </div>
            );
          })}
        </div>
      </div>

      {/* Maisons of selected group */}
      <div className="glass rounded-2xl card-3d p-5 mt-6 border border-white/10">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h2 className="font-display font-bold flex items-center gap-2"><Building2 className="h-5 w-5 text-cyan-400" /> {t("users.groups.maisonsTitle")}</h2>
            <p className="text-sm text-muted-foreground mt-0.5">{selectedGroupId === "all" ? t("users.groups.groupsHint") : (selGroup ? selGroup.name : "")}</p>
          </div>
          <div className="flex flex-col sm:flex-row gap-2">
            {selectedGroupId === "all" && (
              <Select value={newMaisonGroup} onValueChange={setNewMaisonGroup}>
                <SelectTrigger className="h-10 sm:w-48" data-testid="new-maison-group"><SelectValue placeholder={t("users.groups.chooseGroup")} /></SelectTrigger>
                <SelectContent>{groups.map((g) => <SelectItem key={g.id} value={g.id}>{g.name}</SelectItem>)}</SelectContent>
              </Select>
            )}
            <Input value={newMaison} onChange={(e) => setNewMaison(e.target.value)} placeholder={t("users.newMaisonPlaceholder")} data-testid="new-maison-input" className="h-10 sm:w-56" />
            <Button className="h-10 bg-cyan-600 hover:bg-cyan-500 shrink-0 gap-1.5" onClick={openCreateMaison} data-testid="add-maison-btn"><Plus className="h-4 w-4" /> {t("common.add", { defaultValue: "Ajouter" })}</Button>
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 mt-4">
          {visibleMaisons.length === 0 && (
            <div className="col-span-full rounded-2xl border border-dashed border-white/10 p-8 text-center">
              <Building2 className="h-7 w-7 mx-auto text-cyan-400/50 mb-2" />
              <p className="text-sm text-muted-foreground">{t("users.noMaison")}</p>
            </div>
          )}
          {visibleMaisons.map((m) => (
            <div key={m.id} onClick={() => setDrillMaison(m)} className="cursor-pointer rounded-2xl bg-white/[0.03] border border-white/10 p-4 hover:border-cyan-400/40 hover:bg-white/[0.05] transition-colors group/mc" data-testid={`maison-${m.id}`}>
              <div className="flex items-start justify-between">
                <div className="h-9 w-9 rounded-xl bg-cyan-500/15 text-cyan-300 flex items-center justify-center"><Building2 className="h-4 w-4" /></div>
                <div className="flex gap-1">
                  <button onClick={(e) => { e.stopPropagation(); openRename(m); }} className="h-7 w-7 rounded-full hover:bg-white/10 flex items-center justify-center" data-testid={`rename-maison-${m.id}`}><Pencil className="h-3.5 w-3.5" /></button>
                  <button onClick={(e) => { e.stopPropagation(); resetMaison(m); }} title={t("users.resetMaison", { defaultValue: "Réinitialiser (vider prospects/visites/admissions, garder les chambres)" })} className="h-7 w-7 rounded-full hover:bg-amber-500/20 hover:text-amber-300 flex items-center justify-center" data-testid={`reset-maison-${m.id}`}><RotateCcw className="h-3.5 w-3.5" /></button>
                  <button onClick={(e) => { e.stopPropagation(); delMaison(m); }} className="h-7 w-7 rounded-full hover:bg-red-500/20 hover:text-red-400 flex items-center justify-center" data-testid={`del-maison-${m.id}`}><Trash2 className="h-3.5 w-3.5" /></button>
                </div>
              </div>
              <p className="font-semibold mt-3 truncate">{m.name}</p>
              {(m.commune || m.region) && <p className="text-[11px] text-muted-foreground truncate mt-0.5">{[m.commune, regionLabel(m.region)].filter(Boolean).join(" · ")}</p>}
              <div className="flex items-center justify-between mt-1">
                {selectedGroupId === "all" ? <p className="text-xs text-muted-foreground truncate">{m.group_name}</p> : <span />}
                <span className="text-[11px] text-cyan-300/90 inline-flex items-center gap-1 opacity-0 group-hover/mc:opacity-100 transition-opacity"><Users className="h-3 w-3" /> {users.filter((u) => u.maison_id === m.id).length}</span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Users of selected group */}
      <div className="mt-8" data-tour="users-list">
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-display font-bold flex items-center gap-2"><Users className="h-5 w-5 text-indigo-400" /> {t("users.title")} <span className="text-sm text-muted-foreground font-normal">({visibleUsers.length})</span></h2>
          <Button className="h-10 rounded-full gap-1.5" onClick={openCreateUser} data-testid="add-user-btn-2"><UserPlus className="h-4 w-4" /> {t("users.createUser")}</Button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {visibleUsers.length === 0 && (
            <div className="col-span-full glass rounded-2xl p-10 text-center border border-dashed border-white/10">
              <Users className="h-8 w-8 mx-auto text-indigo-400/50 mb-2" />
              <p className="text-muted-foreground">{t("users.noUsersInGroup", { defaultValue: "Aucun utilisateur dans ce groupe." })}</p>
            </div>
          )}
          {visibleUsers.map((u, i) => (
          <motion.div key={u.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3, delay: Math.min(i * 0.04, 0.3) }}
            className={`glass rounded-2xl card-3d p-5 transition-colors ${highlightId === u.id ? "ring-2 ring-indigo-400 border-indigo-400/60" : ""}`} data-testid={`user-${u.id}`}>
            <div className="flex items-start justify-between gap-3">
              <div className="flex items-center gap-3 min-w-0">
                <div className="h-11 w-11 rounded-full flex items-center justify-center text-white font-semibold shrink-0" style={{ background: "linear-gradient(135deg,#8b5cf6,#06b6d4)" }}>
                  {(u.name || "U").split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase()}
                </div>
                <div className="min-w-0">
                  <h3 className="font-display font-bold leading-tight truncate">{u.name}</h3>
                  <p className="text-xs text-muted-foreground font-mono tracking-widest">{u.username}</p>
                </div>
              </div>
              <div className="flex flex-col items-end gap-1.5 shrink-0">
                <span className="text-[11px] font-semibold rounded-full px-2.5 py-1 bg-indigo-500/20 text-indigo-300">{roleLabel(u.role)}</span>
                {!u.active && <span className="text-[11px] font-semibold rounded-full px-2.5 py-1 bg-red-500/20 text-red-300">{t("users.disabled")}</span>}
                {u.must_change_password && u.active && <span className="text-[11px] rounded-full px-2.5 py-1 bg-amber-500/20 text-amber-300">{t("users.tempPwd")}</span>}
              </div>
            </div>
            <p className="text-sm text-muted-foreground mt-3 flex items-center gap-1.5"><Building2 className="h-4 w-4" /> {u.maison}</p>
            {u.email && <p className="text-sm text-muted-foreground flex items-center gap-1.5 mt-0.5"><Mail className="h-4 w-4" /> {u.email}</p>}
            {u.role !== "admin" && (
              <div className="flex gap-2 mt-4 no-print flex-wrap">
                <Button variant="outline" size="sm" className="h-9" onClick={() => openEdit(u)} data-testid={`edit-user-${u.id}`}><Pencil className="h-3.5 w-3.5 mr-1.5" /> {t("users.edit", { defaultValue: "Modifier" })}</Button>
                <Button variant="outline" size="sm" className="h-9" onClick={() => reset(u)} data-testid={`reset-${u.id}`}><KeyRound className="h-3.5 w-3.5 mr-1.5" /> {t("users.reset")}</Button>
                <Button variant="outline" size="sm" className="h-9" onClick={() => setConfirmResend(u)} disabled={resending === u.id || !u.email} title={!u.email ? t("users.resendNoEmail", { defaultValue: "Aucune adresse e-mail" }) : ""} data-testid={`resend-${u.id}`}><Send className="h-3.5 w-3.5 mr-1.5" /> {resending === u.id ? t("users.resending", { defaultValue: "Envoi…" }) : t("users.resendCreds", { defaultValue: "Renvoyer les identifiants" })}</Button>
                <Button variant="outline" size="sm" className="h-9" onClick={() => toggle(u)} data-testid={`toggle-${u.id}`}><Power className="h-3.5 w-3.5 mr-1.5" /> {u.active ? t("users.deactivate") : t("users.activate")}</Button>
                <Button variant="ghost" size="sm" className="h-9 text-red-400 hover:bg-red-500/10" onClick={() => remove(u)} data-testid={`del-user-${u.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
              </div>
            )}
          </motion.div>
          ))}
        </div>
      </div>

      {/* Create user dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-display">{t("users.createTitle")}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label>{t("users.fullName")}</Label>
              <Input data-testid="user-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder={t("users.namePlaceholder")} />
              <p className="text-xs text-muted-foreground">{t("users.nameHint")}</p>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>{t("users.role")}</Label>
                <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                  <SelectTrigger data-testid="user-role"><SelectValue /></SelectTrigger>
                  <SelectContent>{renderRoleOptions()}</SelectContent>
                </Select>
              </div>
              {GROUP_WIDE_ROLES.includes(form.role) ? (
                <div className="space-y-2">
                  <Label>{t("users.groups.groups", { defaultValue: "Groupe" })}</Label>
                  <Select value={form.group_id} onValueChange={(v) => setForm({ ...form, group_id: v })}>
                    <SelectTrigger data-testid="user-group"><SelectValue placeholder={t("users.groups.chooseGroup")} /></SelectTrigger>
                    <SelectContent>{groups.map((g) => <SelectItem key={g.id} value={g.id}>{g.name} · {typeLabel(g.type)}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              ) : (
                <div className="space-y-2">
                  <Label>{t("users.maison")}</Label>
                  <Select value={form.maison} onValueChange={(v) => setForm({ ...form, maison: v })}>
                    <SelectTrigger data-testid="user-maison"><SelectValue placeholder={t("users.choose")} /></SelectTrigger>
                    <SelectContent>{maisons.map((m) => <SelectItem key={m.id} value={m.name}>{m.name} · {m.group_name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              )}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>{t("users.email")}</Label>
                <Input type="email" data-testid="user-email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="prenom.nom@caredesk.be" />
              </div>
              <div className="space-y-2">
                <Label>{t("users.emailLanguage")}</Label>
                <Select value={form.language} onValueChange={(v) => setForm({ ...form, language: v })}>
                  <SelectTrigger data-testid="user-language"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="fr">{t("languages.fr", { defaultValue: "Français" })}</SelectItem>
                    <SelectItem value="nl">{t("languages.nl", { defaultValue: "Néerlandais" })}</SelectItem>
                    <SelectItem value="en">{t("languages.en", { defaultValue: "Anglais" })}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <p className="text-xs text-muted-foreground flex items-start gap-1.5"><Mail className="h-3.5 w-3.5 mt-0.5 shrink-0 text-indigo-300" /> {t("users.emailCredHint")}</p>
            {PROFESSIONAL_ROLES.includes(form.role) && (
              <div className="space-y-4 rounded-xl border border-violet-400/20 bg-violet-500/5 p-3" data-testid="pro-fields-create">
                <div className="space-y-2">
                  <Label>{t("users.function", { defaultValue: "Fonction (affichée dans le planning)" })}</Label>
                  <Select value={form.function || ""} onValueChange={(v) => setForm({ ...form, function: v })}>
                    <SelectTrigger data-testid="user-function"><SelectValue placeholder={t("users.choose")} /></SelectTrigger>
                    <SelectContent>{PROFESSIONAL_FUNCTIONS.map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>{t("users.extraAccess", { defaultValue: "Accès supplémentaires (droits par exception)" })}</Label>
                  <p className="text-[11px] text-muted-foreground">{t("users.extraAccessHint", { defaultValue: "Par défaut : Planning activité + Agenda. Cochez pour donner accès à un module précis." })}</p>
                  <div className="grid grid-cols-2 gap-1.5">
                    {EXTRA_GRANTABLE_MODULES.map((mod) => {
                      const on = (form.extra_allowed_paths || []).includes(mod.to);
                      return (
                        <button type="button" key={mod.to} data-testid={`extra-path-${mod.to}`} onClick={() => setForm({ ...form, extra_allowed_paths: on ? form.extra_allowed_paths.filter((p) => p !== mod.to) : [...(form.extra_allowed_paths || []), mod.to] })}
                          className={`text-left text-xs rounded-lg px-2.5 py-1.5 border transition-colors ${on ? "border-violet-400/60 bg-violet-500/15 text-violet-200" : "border-white/10 bg-white/[0.03] hover:border-white/25"}`}>
                          {on ? "✓ " : ""}{mod.label}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}
            {maisons.length === 0 && <p className="text-xs text-amber-300">{t("users.addMaisonFirst")}</p>}
          </div>
          <DialogFooter><Button onClick={create} data-testid="user-create">{t("users.createBtn")}</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Drill-down: users of a maison */}
      <Dialog open={!!drillMaison} onOpenChange={(o) => !o && setDrillMaison(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2">
              <span className="h-9 w-9 rounded-xl bg-cyan-500/15 text-cyan-300 flex items-center justify-center"><Building2 className="h-4 w-4" /></span>
              {drillMaison?.name}
            </DialogTitle>
            <p className="text-sm text-muted-foreground">{drillMaison?.group_name}</p>
          </DialogHeader>
          {drillMaison && (() => {
            const list = users.filter((u) => u.maison_id === drillMaison.id);
            return (
              <div className="space-y-2 max-h-[55vh] overflow-y-auto pr-1" data-testid="maison-drill-list">
                {list.length === 0 && (
                  <div className="rounded-2xl border border-dashed border-white/10 p-8 text-center">
                    <Users className="h-7 w-7 mx-auto text-indigo-400/50 mb-2" />
                    <p className="text-sm text-muted-foreground">{t("users.noUsersInMaison", { defaultValue: "Aucun utilisateur rattaché à cette maison." })}</p>
                  </div>
                )}
                {list.map((u) => (
                  <div key={u.id} className="flex items-center gap-3 rounded-2xl bg-white/[0.03] border border-white/10 p-3" data-testid={`drill-user-${u.id}`}>
                    <div className="h-10 w-10 rounded-full flex items-center justify-center text-white font-semibold shrink-0" style={{ background: "linear-gradient(135deg,#8b5cf6,#06b6d4)" }}>
                      {(u.name || "U").split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase()}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="font-semibold leading-tight truncate">{u.name}</p>
                      <p className="text-xs text-muted-foreground font-mono tracking-widest">{u.username}</p>
                      {u.email && <p className="text-xs text-muted-foreground truncate flex items-center gap-1 mt-0.5"><Mail className="h-3 w-3" /> {u.email}</p>}
                    </div>
                    <div className="flex flex-col items-end gap-1 shrink-0">
                      <span className="text-[11px] font-semibold rounded-full px-2.5 py-1 bg-indigo-500/20 text-indigo-300">{roleLabel(u.role)}</span>
                      {!u.active && <span className="text-[10px] rounded-full px-2 py-0.5 bg-red-500/20 text-red-300">{t("users.disabled")}</span>}
                    </div>
                    <Button variant="ghost" size="sm" className="h-8 shrink-0" onClick={() => setConfirmResend(u)} disabled={resending === u.id || !u.email} title={t("users.resendCreds", { defaultValue: "Renvoyer les identifiants" })} data-testid={`drill-resend-${u.id}`}><Send className="h-3.5 w-3.5" /></Button>
                    <Button variant="ghost" size="sm" className="h-8 shrink-0" onClick={() => { setDrillMaison(null); openEdit(u); }} data-testid={`drill-edit-${u.id}`}><Pencil className="h-3.5 w-3.5" /></Button>
                  </div>
                ))}
              </div>
            );
          })()}
          <DialogFooter>
            <Button variant="outline" onClick={() => { const m = drillMaison; setDrillMaison(null); setForm({ name: "", role: "directeur", maison: m?.name || "", group_id: m?.group_id || "", email: "", language: "fr", function: "", extra_allowed_paths: [] }); setOpen(true); }} data-testid="drill-add-user"><UserPlus className="h-4 w-4 mr-1.5" /> {t("users.createUser")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirm resend credentials */}
      <AlertDialog open={!!confirmResend} onOpenChange={(o) => !o && setConfirmResend(null)}>
        <AlertDialogContent data-testid="confirm-resend-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2"><Send className="h-5 w-5 text-indigo-400" /> {t("users.resendConfirmTitle", { defaultValue: "Confirmer le renvoi ?" })}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("users.resendConfirmDesc", { name: confirmResend?.name, email: confirmResend?.email, defaultValue: `Un nouvel identifiant et mot de passe seront envoyés à ${confirmResend?.email}. L'ancien mot de passe de ${confirmResend?.name} sera immédiatement invalidé.` })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="confirm-resend-cancel">{t("common.cancel", { defaultValue: "Annuler" })}</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-resend-ok" onClick={() => { const u = confirmResend; setConfirmResend(null); if (u) resendCreds(u); }}>
              {t("users.resendConfirmBtn", { defaultValue: "Oui, renvoyer" })}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Edit user dialog */}
      <Dialog open={!!editUser} onOpenChange={(o) => !o && setEditUser(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-display">{t("users.edit", { defaultValue: "Modifier l'utilisateur" })} — {editUser?.username}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label>{t("users.fullName")}</Label>
              <Input data-testid="edit-user-name" value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>{t("users.role")}</Label>
                <Select value={editForm.role} onValueChange={(v) => setEditForm({ ...editForm, role: v })}>
                  <SelectTrigger data-testid="edit-user-role"><SelectValue /></SelectTrigger>
                  <SelectContent>{renderRoleOptions()}</SelectContent>
                </Select>
              </div>
              {GROUP_WIDE_ROLES.includes(editForm.role) ? (
                <div className="space-y-2">
                  <Label>{t("users.groups.groups", { defaultValue: "Groupe" })}</Label>
                  <Select value={editForm.group_id} onValueChange={(v) => setEditForm({ ...editForm, group_id: v })}>
                    <SelectTrigger data-testid="edit-user-group"><SelectValue placeholder={t("users.groups.chooseGroup")} /></SelectTrigger>
                    <SelectContent>{groups.map((g) => <SelectItem key={g.id} value={g.id}>{g.name} · {typeLabel(g.type)}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              ) : (
                <div className="space-y-2">
                  <Label>{t("users.maison")}</Label>
                  <Select value={editForm.maison} onValueChange={(v) => setEditForm({ ...editForm, maison: v })}>
                    <SelectTrigger data-testid="edit-user-maison"><SelectValue placeholder={t("users.choose")} /></SelectTrigger>
                    <SelectContent>{maisons.map((m) => <SelectItem key={m.id} value={m.name}>{m.name} · {m.group_name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              )}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>{t("users.email")}</Label>
                <Input type="email" data-testid="edit-user-email" value={editForm.email} onChange={(e) => setEditForm({ ...editForm, email: e.target.value })} placeholder="prenom.nom@caredesk.be" />
              </div>
              <div className="space-y-2">
                <Label>{t("users.emailLanguage")}</Label>
                <Select value={editForm.language} onValueChange={(v) => setEditForm({ ...editForm, language: v })}>
                  <SelectTrigger data-testid="edit-user-language"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="fr">{t("languages.fr", { defaultValue: "Français" })}</SelectItem>
                    <SelectItem value="nl">{t("languages.nl", { defaultValue: "Néerlandais" })}</SelectItem>
                    <SelectItem value="en">{t("languages.en", { defaultValue: "Anglais" })}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            {PROFESSIONAL_ROLES.includes(editForm.role) && (
              <div className="space-y-4 rounded-xl border border-violet-400/20 bg-violet-500/5 p-3" data-testid="pro-fields-edit">
                <div className="space-y-2">
                  <Label>{t("users.function", { defaultValue: "Fonction (affichée dans le planning)" })}</Label>
                  <Select value={editForm.function || ""} onValueChange={(v) => setEditForm({ ...editForm, function: v })}>
                    <SelectTrigger data-testid="edit-user-function"><SelectValue placeholder={t("users.choose")} /></SelectTrigger>
                    <SelectContent>{PROFESSIONAL_FUNCTIONS.map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>{t("users.extraAccess", { defaultValue: "Accès supplémentaires (droits par exception)" })}</Label>
                  <p className="text-[11px] text-muted-foreground">{t("users.extraAccessHint", { defaultValue: "Par défaut : Planning activité + Agenda. Cochez pour donner accès à un module précis." })}</p>
                  <div className="grid grid-cols-2 gap-1.5">
                    {EXTRA_GRANTABLE_MODULES.map((mod) => {
                      const on = (editForm.extra_allowed_paths || []).includes(mod.to);
                      return (
                        <button type="button" key={mod.to} data-testid={`edit-extra-path-${mod.to}`} onClick={() => setEditForm({ ...editForm, extra_allowed_paths: on ? editForm.extra_allowed_paths.filter((p) => p !== mod.to) : [...(editForm.extra_allowed_paths || []), mod.to] })}
                          className={`text-left text-xs rounded-lg px-2.5 py-1.5 border transition-colors ${on ? "border-violet-400/60 bg-violet-500/15 text-violet-200" : "border-white/10 bg-white/[0.03] hover:border-white/25"}`}>
                          {on ? "✓ " : ""}{mod.label}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}
          </div>
          <DialogFooter><Button onClick={saveEdit} data-testid="edit-user-save">{t("crm.actions.save", { defaultValue: "Enregistrer" })}</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reminders dialog */}
      <Dialog open={reminderOpen} onOpenChange={setReminderOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-display flex items-center gap-2"><Mail className="h-5 w-5 text-pink-400" /> {t("users.reminderTitle")}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <p className="text-sm text-muted-foreground">{t("users.reminderIntro")}</p>
            <div className="space-y-2">
              <Label>{t("users.emailOptional")}</Label>
              <Input type="email" data-testid="reminder-email" value={reminderEmail} onChange={(e) => setReminderEmail(e.target.value)} placeholder={t("users.reminderPlaceholder")} />
            </div>
          </div>
          <DialogFooter><Button onClick={sendReminders} data-testid="reminder-send"><Send className="h-4 w-4 mr-2" /> {t("users.send")}</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Rename maison dialog */}
      <Dialog open={!!renameTarget} onOpenChange={(o) => !o && setRenameTarget(null)}>
        <DialogContent className="max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display flex items-center gap-2"><Building2 className="h-5 w-5 text-cyan-400" /> {renameTarget?.id ? t("users.renameTitle") : t("users.addMaisonTitle", { defaultValue: "Ajouter une maison" })}</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            {renameTarget?.id && <p className="text-sm text-muted-foreground">{t("users.renameIntro")}</p>}
            <div className="space-y-2">
              <Label>{t("users.newName")}</Label>
              <Input data-testid="rename-maison-input" value={renameValue} autoFocus
                onChange={(e) => setRenameValue(e.target.value)}
                placeholder={t("users.renamePlaceholder")} />
            </div>
            <div className="space-y-2">
              <Label>{t("users.groups.reassign")}</Label>
              <Select value={renameGroup} onValueChange={setRenameGroup}>
                <SelectTrigger data-testid="rename-maison-group"><SelectValue placeholder={t("users.groups.chooseGroup")} /></SelectTrigger>
                <SelectContent>{groups.map((g) => <SelectItem key={g.id} value={g.id}>{g.name} · {typeLabel(g.type)}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>{t("users.maisonRegion", { defaultValue: "Région" })}</Label>
                <Select value={mRegion} onValueChange={setMRegion}>
                  <SelectTrigger data-testid="maison-region"><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent>{REGIONS.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>{t("users.maisonCommune", { defaultValue: "Commune" })}</Label>
                <Input data-testid="maison-commune" value={mCommune} onChange={(e) => setMCommune(e.target.value)} placeholder="Ex : Anderlecht" />
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>{t("users.maisonWebsite", { defaultValue: "Site internet officiel" })}</Label>
                <Input data-testid="maison-website" value={mWebsite} onChange={(e) => setMWebsite(e.target.value)} placeholder="https://…" />
              </div>
              <div className="space-y-2">
                <Label>{t("users.maisonPhone", { defaultValue: "Numéro de contact" })}</Label>
                <Input data-testid="maison-phone" value={mPhone} onChange={(e) => setMPhone(e.target.value)} placeholder="Ex : 02 000 00 00" />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRenameTarget(null)}>{t("common.cancel")}</Button>
            <Button onClick={saveMaison} data-testid="rename-maison-confirm">{renameTarget?.id ? t("users.rename") : t("common.add", { defaultValue: "Ajouter" })}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit group dialog */}
      <Dialog open={!!editGroup} onOpenChange={(o) => !o && setEditGroup(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-display flex items-center gap-2"><Layers className="h-5 w-5 text-violet-400" /> {t("users.groups.editGroup")}</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <div className="space-y-2">
              <Label>{t("users.groups.groupName")}</Label>
              <Input data-testid="edit-group-input" value={editGroup?.name || ""} onChange={(e) => setEditGroup({ ...editGroup, name: e.target.value })} />
            </div>
            <div className="space-y-2">
              <Label>{t("users.groups.groupType")}</Label>
              <Select value={editGroup?.type || "prive"} onValueChange={(v) => setEditGroup({ ...editGroup, type: v })}>
                <SelectTrigger data-testid="edit-group-type"><SelectValue /></SelectTrigger>
                <SelectContent>{Object.keys(GROUP_TYPES).map((tp) => <SelectItem key={tp} value={tp}>{typeLabel(tp)}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditGroup(null)}>{t("common.cancel")}</Button>
            <Button onClick={saveGroup} data-testid="edit-group-save" className="bg-violet-600 hover:bg-violet-500">{t("crm.actions.save", { defaultValue: "Enregistrer" })}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {creds && <Credentials creds={creds} onClose={() => setCreds(null)} />}
    </div>
  );
}
